import type { AlgoliaProductHit } from '../../../types/algolia';
import type { ReactNode } from 'react';

import type { Content } from '~/amplienceTypes/schemas/exported/banner-schema';

export type Position = {
  desktop: number;
  mobile: number;
  tablet: number;
};

export type ProductTileData = {
  type: 'product';
  key: string;
  position: number;
  component: ReactNode;
};

export type InjectedContentData = {
  type: 'injectedContent';
  key: string;
  position: number;
  component: ReactNode;
  content: Content;
};

export type GridItem = ProductTileData | InjectedContentData;

export type FilteredInjectedContent = {
  content: Content;
  position: Position | undefined;
};

export type Breakpoint = 'mobile' | 'tablet' | 'desktop';

export function getGridColumnsPerRow(
  breakpoint: Breakpoint,
  desktopColumns: number,
  mobileColumns: number
): number {
  if (breakpoint === 'desktop') {
    return desktopColumns;
  }

  return mobileColumns;
}

export function getInjectedContentPositionAndSpan(
  content: Content,
  position: Position | undefined,
  gridColumns: number
): { position: number; span: number } {
  let currentPosition = 0;
  let currentSpan = 1;

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const contentSize = (content as any)?.size;

  if (gridColumns === 4) {
    currentPosition = position?.desktop ?? 10;
    currentSpan = contentSize?.desktop ?? 2;
  } else if (gridColumns === 3) {
    currentPosition = position?.tablet ?? 7;
    currentSpan = contentSize?.tablet ?? 2;
  } else if (gridColumns === 2) {
    currentPosition = position?.mobile ?? 5;
    currentSpan = contentSize?.mobile ?? 2;
  } else {
    currentPosition = position?.mobile ?? 5;
  }

  return { position: currentPosition, span: currentSpan };
}

export function buildInjectedContentMap(
  filteredInjectedContent: FilteredInjectedContent[] | undefined,
  gridColumns: number,
  productsAmount: number,
  contentSize: number
): Map<number, { span: number; content: Content; position: Position | undefined }> {
  const injectedContentMap = new Map<
    number,
    { span: number; content: Content; position: Position | undefined }
  >();

  if (!filteredInjectedContent) {
    return injectedContentMap;
  }

  filteredInjectedContent.forEach(({ content, position }) => {
    if (!content) {
      return;
    }

    const { position: currentPosition, span: currentSpan } = getInjectedContentPositionAndSpan(
      content,
      position,
      gridColumns
    );

    if (currentPosition > 0 && currentPosition <= productsAmount + contentSize) {
      injectedContentMap.set(currentPosition, { span: currentSpan, content, position });
    }
  });

  return injectedContentMap;
}

export function buildOrderedItems(
  hits: AlgoliaProductHit[],
  injectedContentMap: Map<
    number,
    { span: number; content: Content; position: Position | undefined }
  >,
  renderProductTile: (hit: AlgoliaProductHit, index: number) => ReactNode,
  renderInjectedContent: (
    content: Content,
    position: Position | undefined,
    deliveryId: string
  ) => ReactNode
): GridItem[] {
  let productIndex = 0;
  let gridPosition = 1;

  return hits.reduce<GridItem[]>((acc, hit) => {
    const injectedAtPosition = injectedContentMap.get(gridPosition);

    if (injectedAtPosition) {
      const { span, content, position } = injectedAtPosition;

      acc.push({
        type: 'injectedContent',
        key: String(content._meta.deliveryId),
        position: gridPosition,
        component: renderInjectedContent(content, position, String(content._meta.deliveryId)),
        content,
      });

      gridPosition += span;
    }

    acc.push({
      type: 'product',
      key: hit.objectID,
      position: gridPosition,
      component: renderProductTile(hit, productIndex),
    });

    productIndex++;
    gridPosition++;

    return acc;
  }, []);
}
